import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_HAS_LISTABLE_STOCK_FILTER, ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS, ALGOLIA_PRODUCT_INDEXES } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import ProductListingPageDescriptionUI from '../ProductListingPageDescriptionUI';
import AdvancedSearchSidebar from '../AdvancedSearchSidebar';
import AdvancedSearchProductHits from '../AdvancedSearchProductHits';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import useAdvancedSearchURL from '../../hooks/useAdvancedSearchURL';
import CustomerPhotos from '../../pages/CustomerPhotos';
import CustomerReviews from '../../pages/CustomerReviews';
import StaticPages from '../../pages/StaticPages';
import DesktopProductListingColorFilter from './components/DesktopProductListingColorFilter';
import ProductListingSortBy from './components/ProductListingSortBy';
import MobileProductFilters from './components/MobileProductFilters';

// Helper component to sync query from parent to nested Index
const VirtualSearchBox = t0 => {
  const $ = _c(5);
  const {
    parentQuery
  } = t0;
  const {
    query,
    refine
  } = useSearchBox();
  let t1;
  let t2;
  if ($[0] !== parentQuery || $[1] !== query || $[2] !== refine) {
    t1 = () => {
      if (parentQuery && parentQuery !== query) {
        refine(parentQuery);
      }
    };
    t2 = [parentQuery, query, refine];
    $[0] = parentQuery;
    $[1] = query;
    $[2] = refine;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
};
const SectionResultsCountTracker = t0 => {
  const $ = _c(6);
  const {
    onCountChange
  } = t0;
  const {
    results
  } = useInstantSearch();
  let t1;
  if ($[0] !== onCountChange || $[1] !== results?.nbHits) {
    t1 = () => {
      const count = results?.nbHits;
      if (Number.isFinite(count)) {
        onCountChange(count);
      }
    };
    $[0] = onCountChange;
    $[1] = results?.nbHits;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = results?.nbHits;
  let t3;
  if ($[3] !== onCountChange || $[4] !== t2) {
    t3 = [t2, onCountChange];
    $[3] = onCountChange;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t1, t3);
  return null;
};
const AlgoliaHits = t0 => {
  const $ = _c(92);
  const {
    loading: t1,
    pageName: t2,
    variant: t3,
    isFinalProductCountKnown: t4,
    isDesktopSidebarOpen: t5,
    pageLongDescription: t6
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const pageName = t2 === undefined ? "" : t2;
  const variant = t3 === undefined ? "advanced-search" : t3;
  const isFinalProductCountKnown = t4 === undefined ? true : t4;
  const isDesktopSidebarOpen = t5 === undefined ? true : t5;
  const pageLongDescription = t6 === undefined ? "" : t6;
  const {
    isMobile
  } = useScreen();
  const {
    currentSearchSection,
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  const {
    results,
    status
  } = useInstantSearch();
  const {
    query: parentQuery
  } = useSearchBox();
  const {
    queryUrlParam
  } = useAdvancedSearchURL();
  const [activeSectionCount, setActiveSectionCount] = useState(results?.nbHits || 0);
  const [delayedResultsCount, setDelayedResultsCount] = useState(null);
  const isSearchPending = status === "loading" || status === "stalled";
  const getCustomProps = _temp2;
  const [isSidebarOpen, setIsDesktopSidebarOpen] = useState(isDesktopSidebarOpen);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsDesktopSidebarOpen(_temp3);
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const toggleSidebar = t7;
  const searchTerm = parentQuery || results?.query || queryUrlParam || pageName || "";
  const displayText = searchTerm ? `"${searchTerm}"` : "\"\"";
  const productsCount = results?.nbHits || 0;
  let t8;
  if ($[1] !== currentProductIndex || $[2] !== currentSearchSection || $[3] !== results) {
    t8 = () => {
      if (currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && Number.isFinite(results?.nbHits)) {
        setActiveSectionCount(results.nbHits);
      }
    };
    $[1] = currentProductIndex;
    $[2] = currentSearchSection;
    $[3] = results;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const t9 = results?.nbHits;
  let t10;
  if ($[5] !== currentProductIndex || $[6] !== currentSearchSection || $[7] !== t9) {
    t10 = [currentSearchSection, currentProductIndex, t9];
    $[5] = currentProductIndex;
    $[6] = currentSearchSection;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  useEffect(t8, t10);
  let t11;
  let t12;
  if ($[9] !== activeSectionCount || $[10] !== isFinalProductCountKnown || $[11] !== isSearchPending) {
    t11 = () => {
      if (isSearchPending || !isFinalProductCountKnown) {
        setDelayedResultsCount(null);
        return;
      }
      if (activeSectionCount > 0) {
        const timer = setTimeout(() => {
          setDelayedResultsCount(activeSectionCount);
        }, 750);
        return () => clearTimeout(timer);
      }
      setDelayedResultsCount(null);
    };
    t12 = [activeSectionCount, isSearchPending, isFinalProductCountKnown, 750];
    $[9] = activeSectionCount;
    $[10] = isFinalProductCountKnown;
    $[11] = isSearchPending;
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  useEffect(t11, t12);
  const resultsCountText = !loading && isFinalProductCountKnown && delayedResultsCount != null && delayedResultsCount > 0 ? `${delayedResultsCount} Results` : null;
  let t13;
  if ($[14] !== isReadyToShipActive) {
    t13 = isReadyToShipActive ? ["is_in_stock:true"] : [];
    $[14] = isReadyToShipActive;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const secondaryFacetFilters = t13;
  let filters;
  if ($[16] !== maxWidth || $[17] !== minWidth) {
    filters = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    $[16] = maxWidth;
    $[17] = minWidth;
    $[18] = filters;
  } else {
    filters = $[18];
  }
  const secondaryNumericFilters = filters;
  const t14 = variant === "advanced-search";
  const t15 = variant === "advanced-search";
  const t16 = !variant || variant !== "advanced-search";
  let t17;
  if ($[19] !== t15 || $[20] !== t16) {
    t17 = classNames("px-3 py-4 space-y-1", "md:px-[3.333vw] md:py-0 md:space-y-0 min-[1500px]:px-[50px]", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:mt-4", "pt-0 md:pt-0", {
      "md:top-[55px]": t15,
      "md:top-[117px]": t16
    });
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== resultsCountText) {
    t18 = resultsCountText && <span className="text-sm text-gray-light2 font-normal ml-3">{resultsCountText}</span>;
    $[22] = resultsCountText;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== displayText || $[25] !== t18) {
    t19 = <>Results for {displayText}{t18}</>;
    $[24] = displayText;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = variant === "advanced-search";
  let t21;
  if ($[27] !== t19 || $[28] !== t20) {
    t21 = <div className="md:flex md:gap-2 md:items-center md:flex-nowrap min-w-0"><ProductListingPageName pageName={t19} showPageNameWhileLoading={t20} /></div>;
    $[27] = t19;
    $[28] = t20;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== loading) {
    t22 = <DesktopShowHideFiltersButton loading={loading} onClick={toggleSidebar} />;
    $[30] = loading;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t21 || $[33] !== t22) {
    t23 = <div className="md:flex md:gap-5 md:items-center min-w-0">{t21}{t22}</div>;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  let t25;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t25 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[35] = t24;
    $[36] = t25;
  } else {
    t24 = $[35];
    t25 = $[36];
  }
  let t26;
  if ($[37] !== currentSearchSection || $[38] !== isMobile || $[39] !== productsCount) {
    t26 = ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) && isMobile && <><MobileProductFilters productsCount={productsCount} /><div className="flex-1 min-w-[140px] flex items-center"><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp4)} /></div></>;
    $[37] = currentSearchSection;
    $[38] = isMobile;
    $[39] = productsCount;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== currentSearchSection || $[42] !== isMobile) {
    t27 = ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) && !isMobile && <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp5)} /></div>;
    $[41] = currentSearchSection;
    $[42] = isMobile;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t26 || $[45] !== t27) {
    t28 = <div className={t25}>{t26}{t27}</div>;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <MobileGridColumnToggle />;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== t28) {
    t30 = <div className={t24}>{t28}{t29}</div>;
    $[48] = t28;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== t17 || $[51] !== t23 || $[52] !== t30) {
    t31 = <div className={t17}>{t23}{t30}</div>;
    $[50] = t17;
    $[51] = t23;
    $[52] = t30;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== isMobile || $[55] !== isSidebarOpen) {
    t32 = !isMobile && <DesktopProductListingSidebarContainer isOpen={isSidebarOpen}><AdvancedSearchSidebar /></DesktopProductListingSidebarContainer>;
    $[54] = isMobile;
    $[55] = isSidebarOpen;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== currentProductIndex || $[58] !== currentSearchSection) {
    t33 = currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && <><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><AdvancedSearchProductHits /></>;
    $[57] = currentProductIndex;
    $[58] = currentSearchSection;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== currentProductIndex || $[61] !== currentSearchSection || $[62] !== searchTerm || $[63] !== secondaryFacetFilters || $[64] !== secondaryNumericFilters) {
    t34 = currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex !== ALGOLIA_INDEX_NAME.PRODUCTS && <Index indexName={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={searchTerm} /><Configure facets={["*"]} filters={ALGOLIA_HAS_LISTABLE_STOCK_FILTER} facetFilters={secondaryFacetFilters} numericFilters={secondaryNumericFilters} attributesToRetrieve={["_highlightResult", "product_id", "name", "is_in_stock"]} clickAnalytics={true} /><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><AdvancedSearchProductHits /></Index>;
    $[60] = currentProductIndex;
    $[61] = currentSearchSection;
    $[62] = searchTerm;
    $[63] = secondaryFacetFilters;
    $[64] = secondaryNumericFilters;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== currentSearchSection || $[67] !== searchTerm) {
    t35 = currentSearchSection === ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS && <Index indexName={ALGOLIA_INDEX_NAME.CUSTOMER_PHOTOS}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={searchTerm} /><Configure facets={["*"]} filters="" numericFilters="" facetFilters="" attributesToRetrieve={["*"]} /><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><CustomerPhotos getCustomProps={getCustomProps} /></Index>;
    $[66] = currentSearchSection;
    $[67] = searchTerm;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== currentSearchSection || $[70] !== searchTerm) {
    t36 = currentSearchSection === ALGOLIA_INDEX_NAME.REVIEWS && <Index indexName={ALGOLIA_INDEX_NAME.REVIEWS}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={searchTerm} /><Configure facets={["*"]} filters="" numericFilters="" facetFilters="" attributesToRetrieve={["review_id", "review_full_name", "review_rate", "review_comment", "review_title", "review_date", "product_name", "product_url", "sef_url", "image_link", "fk_product_id"]} /><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><CustomerReviews /></Index>;
    $[69] = currentSearchSection;
    $[70] = searchTerm;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== currentSearchSection || $[73] !== searchTerm) {
    t37 = currentSearchSection === ALGOLIA_INDEX_NAME.STATIC_PAGES && <Index indexName={ALGOLIA_INDEX_NAME.STATIC_PAGES}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={searchTerm} /><Configure filters="" numericFilters={["is_active=1", "show_in_search=1", "fk_store_id=1"]} attributesToRetrieve={["*"]} /><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><StaticPages /></Index>;
    $[72] = currentSearchSection;
    $[73] = searchTerm;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== pageLongDescription || $[76] !== pageName) {
    t38 = <ProductListingPageDescriptionUI pageLongDescription={pageLongDescription} pageName={pageName} />;
    $[75] = pageLongDescription;
    $[76] = pageName;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== t33 || $[79] !== t34 || $[80] !== t35 || $[81] !== t36 || $[82] !== t37 || $[83] !== t38) {
    t39 = <div className="md:space-y-8 md:mb-8 md:flex-1">{t33}{t34}{t35}{t36}{t37}{t38}</div>;
    $[78] = t33;
    $[79] = t34;
    $[80] = t35;
    $[81] = t36;
    $[82] = t37;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== t32 || $[86] !== t39) {
    t40 = <div className="md:flex md:flex-row md:gap md:mt-2 md:px-[3.333vw] min-[1500px]:px-[50px]">{t32}{t39}</div>;
    $[85] = t32;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== t14 || $[89] !== t31 || $[90] !== t40) {
    t41 = <FixedWidthContainer wide={true} noMobilePadding={true} noPadding={t14}>{t31}{t40}</FixedWidthContainer>;
    $[88] = t14;
    $[89] = t31;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  return t41;
};
export default AlgoliaHits;
function _temp(p) {
  return p.upholstery;
}
function _temp2(selectedGridItem) {
  if (!selectedGridItem) {
    return {};
  }
  const customerName = `${selectedGridItem.customer_first_name || ""} ${selectedGridItem.customer_last_name && selectedGridItem.customer_last_name[0] ? `${selectedGridItem.customer_last_name[0]}.` : ""}`.trim();
  const products = selectedGridItem.products || [];
  return {
    imageProps: {
      src: transformInspirationImageUrl(selectedGridItem.image_link || selectedGridItem.photo_image || selectedGridItem.photo_url) || "",
      trim: "auto"
    },
    selectedItemName: <CustomerNameWithEmoji>{customerName}</CustomerNameWithEmoji>,
    selectedItemNameMobile: <span className="CustomerPhotosCustomerNameMobile">{customerName}</span>,
    selectedItemProducts: products,
    selectedItemMaterialList: products.map(_temp).filter(Boolean),
    selectedItemDescription: selectedGridItem.photo_comment || selectedGridItem.photo_description || "",
    showDescriptionQuoteMark: true
  };
}
function _temp3(prevIsOpen) {
  return !prevIsOpen;
}
function _temp4(option) {
  return option.value !== "newest";
}
function _temp5(option_0) {
  return option_0.value !== "newest";
}